/**
 * Voice Ordering Service
 * Handles voice session management and communication with the voice ordering backend
 */

import { API_ENDPOINTS } from '../config/apiRouting';
import type {
  VoiceSession,
  VoiceResponse,
  VoiceConfig,
  VoiceAnalytics,
  JetsonDevice,
  CreateVoiceSessionRequest,
  ProcessUtteranceRequest,
  CompleteSessionRequest,
  UpdateVoiceConfigRequest,
  PreRecordedPhrase,
  VoiceOrderingMode
} from '../types/voiceOrdering';

// Get auth token from storage
const getAuthToken = (): string | null => {
  return sessionStorage.getItem('authToken') ||
    localStorage.getItem('authToken') ||
    sessionStorage.getItem('idToken') ||
    localStorage.getItem('idToken');
};

// Default headers
const getHeaders = (): HeadersInit => {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  };

  const token = getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
};

/**
 * Voice Ordering Service
 */
export const voiceOrderingService = {
  // ============================================
  // SESSION MANAGEMENT
  // ============================================

  /**
   * Create a new voice ordering session
   */
  async createSession(request: CreateVoiceSessionRequest): Promise<VoiceSession> {
    const response = await fetch(API_ENDPOINTS.voiceSessions(), {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(request)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || `Failed to create session: ${response.status}`);
    }

    return response.json();
  },

  /**
   * Get an existing voice session
   */
  async getSession(sessionId: string): Promise<VoiceSession> {
    const response = await fetch(API_ENDPOINTS.voiceSession(sessionId), {
      method: 'GET',
      headers: getHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to get session: ${response.status}`);
    }

    return response.json();
  },

  /**
   * Process a voice utterance (text from speech recognition)
   */
  async processUtterance(sessionId: string, text: string, confidence?: number): Promise<VoiceResponse> {
    const request: ProcessUtteranceRequest = { sessionId, text, confidence };

    const response = await fetch(API_ENDPOINTS.voiceSessionUtterance(sessionId), {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(request)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || `Failed to process utterance: ${response.status}`);
    }

    return response.json();
  },

  /**
   * Complete a voice session and create an order
   */
  async completeSession(sessionId: string, tipAmount?: number): Promise<{ orderId: string; order: any }> {
    const request: CompleteSessionRequest = { sessionId, tipAmount };

    const response = await fetch(API_ENDPOINTS.voiceSessionComplete(sessionId), {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(request)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || `Failed to complete session: ${response.status}`);
    }

    return response.json();
  },

  /**
   * Cancel a voice session
   */
  async cancelSession(sessionId: string): Promise<void> {
    const response = await fetch(API_ENDPOINTS.voiceSession(sessionId), {
      method: 'DELETE',
      headers: getHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to cancel session: ${response.status}`);
    }
  },

  /**
   * Run a simulation conversation (for testing)
   */
  async simulate(storeId: string, utterances: string[]): Promise<{ sessionId: string; conversation: VoiceResponse[] }> {
    const response = await fetch(API_ENDPOINTS.voiceSimulate(), {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ storeId, utterances })
    });

    if (!response.ok) {
      throw new Error(`Failed to run simulation: ${response.status}`);
    }

    return response.json();
  },

  // ============================================
  // CONFIGURATION MANAGEMENT
  // ============================================

  /**
   * Get voice configuration for a store
   */
  async getConfig(storeId: string): Promise<VoiceConfig> {
    const response = await fetch(API_ENDPOINTS.voiceConfig(storeId), {
      method: 'GET',
      headers: getHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to get voice config: ${response.status}`);
    }

    return response.json();
  },

  /**
   * Update voice configuration for a store
   */
  async updateConfig(storeId: string, updates: UpdateVoiceConfigRequest): Promise<VoiceConfig> {
    const response = await fetch(API_ENDPOINTS.voiceConfig(storeId), {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(updates)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || `Failed to update voice config: ${response.status}`);
    }

    return response.json();
  },

  // ============================================
  // DEVICE MANAGEMENT
  // ============================================

  /**
   * List Jetson devices for a store
   */
  async listDevices(storeId: string): Promise<{ devices: JetsonDevice[]; count: number }> {
    const response = await fetch(API_ENDPOINTS.voiceDevices(storeId), {
      method: 'GET',
      headers: getHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to list devices: ${response.status}`);
    }

    return response.json();
  },

  /**
   * Register a new Jetson device
   */
  async registerDevice(storeId: string, deviceInfo: Partial<JetsonDevice>): Promise<JetsonDevice> {
    const response = await fetch(API_ENDPOINTS.voiceDevices(storeId), {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(deviceInfo)
    });

    if (!response.ok) {
      throw new Error(`Failed to register device: ${response.status}`);
    }

    return response.json();
  },

  /**
   * Unregister a Jetson device
   */
  async unregisterDevice(storeId: string, deviceId: string): Promise<void> {
    const response = await fetch(API_ENDPOINTS.voiceDevice(storeId, deviceId), {
      method: 'DELETE',
      headers: getHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to unregister device: ${response.status}`);
    }
  },

  /**
   * Trigger menu sync to a Jetson device
   */
  async syncDeviceMenu(storeId: string, deviceId: string): Promise<{ message: string; syncInitiatedAt: string }> {
    const response = await fetch(API_ENDPOINTS.voiceDeviceSync(storeId, deviceId), {
      method: 'POST',
      headers: getHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to sync device menu: ${response.status}`);
    }

    return response.json();
  },

  // ============================================
  // PRE-RECORDED PHRASES
  // ============================================

  /**
   * Upload a pre-recorded phrase
   */
  async uploadPhrase(
    storeId: string,
    phraseKey: string,
    phraseText: string,
    category: string,
    audioBase64: string
  ): Promise<PreRecordedPhrase> {
    const response = await fetch(API_ENDPOINTS.voicePhrases(storeId), {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ phraseKey, phraseText, category, audioBase64 })
    });

    if (!response.ok) {
      throw new Error(`Failed to upload phrase: ${response.status}`);
    }

    return response.json();
  },

  /**
   * Delete a pre-recorded phrase
   */
  async deletePhrase(storeId: string, phraseKey: string): Promise<void> {
    const response = await fetch(API_ENDPOINTS.voicePhrase(storeId, phraseKey), {
      method: 'DELETE',
      headers: getHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to delete phrase: ${response.status}`);
    }
  },

  // ============================================
  // ANALYTICS
  // ============================================

  /**
   * Get voice ordering analytics for a store
   */
  async getAnalytics(storeId: string, period: 'day' | 'week' | 'month' = 'day'): Promise<VoiceAnalytics> {
    const url = `${API_ENDPOINTS.voiceAnalytics(storeId)}?period=${period}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: getHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to get analytics: ${response.status}`);
    }

    return response.json();
  }
};

/**
 * Voice Session Manager
 * Stateful wrapper for managing an active voice session
 */
export class VoiceSessionManager {
  private session: VoiceSession | null = null;
  private onStateChange?: (session: VoiceSession) => void;
  private onResponse?: (response: VoiceResponse) => void;
  private onError?: (error: Error) => void;

  constructor(options?: {
    onStateChange?: (session: VoiceSession) => void;
    onResponse?: (response: VoiceResponse) => void;
    onError?: (error: Error) => void;
  }) {
    this.onStateChange = options?.onStateChange;
    this.onResponse = options?.onResponse;
    this.onError = options?.onError;
  }

  /**
   * Start a new voice session
   */
  async start(
    storeId: string,
    mode: VoiceOrderingMode,
    options?: { deviceId?: string; tableId?: string; guestCheckId?: string; serverId?: string }
  ): Promise<VoiceSession> {
    try {
      this.session = await voiceOrderingService.createSession({
        storeId,
        deviceId: options?.deviceId || 'web-simulator',
        mode,
        tableId: options?.tableId,
        guestCheckId: options?.guestCheckId,
        serverId: options?.serverId
      });

      this.onStateChange?.(this.session);

      // The initial session includes a greeting response
      if (this.session && this.onResponse) {
        this.onResponse({
          sessionId: this.session.sessionId,
          state: this.session.state,
          responseText: 'Welcome! What can I get for you today?',
          responseType: 'tts',
          currentOrder: this.session.currentOrder,
          requiresConfirmation: false
        } as VoiceResponse);
      }

      return this.session;
    } catch (error) {
      this.onError?.(error as Error);
      throw error;
    }
  }

  /**
   * Process spoken text
   */
  async speak(text: string, confidence?: number): Promise<VoiceResponse | null> {
    if (!this.session) {
      const error = new Error('No active session');
      this.onError?.(error);
      throw error;
    }

    try {
      const response = await voiceOrderingService.processUtterance(
        this.session.sessionId,
        text,
        confidence
      );

      // Update local session state
      if (response.state) {
        this.session.state = response.state;
      }
      if (response.currentOrder) {
        this.session.currentOrder = response.currentOrder;
      }

      this.onStateChange?.(this.session);
      this.onResponse?.(response);

      return response;
    } catch (error) {
      this.onError?.(error as Error);
      throw error;
    }
  }

  /**
   * Complete the order
   */
  async complete(tipAmount?: number): Promise<{ orderId: string; order: any } | null> {
    if (!this.session) {
      const error = new Error('No active session');
      this.onError?.(error);
      throw error;
    }

    try {
      const result = await voiceOrderingService.completeSession(
        this.session.sessionId,
        tipAmount
      );

      this.session.state = 'complete';
      this.onStateChange?.(this.session);

      return result;
    } catch (error) {
      this.onError?.(error as Error);
      throw error;
    }
  }

  /**
   * Cancel the session
   */
  async cancel(): Promise<void> {
    if (!this.session) return;

    try {
      await voiceOrderingService.cancelSession(this.session.sessionId);
      this.session.state = 'cancelled';
      this.onStateChange?.(this.session);
    } catch (error) {
      this.onError?.(error as Error);
      throw error;
    } finally {
      this.session = null;
    }
  }

  /**
   * Get current session
   */
  getSession(): VoiceSession | null {
    return this.session;
  }

  /**
   * Check if session is active
   */
  isActive(): boolean {
    return this.session !== null &&
      this.session.state !== 'complete' &&
      this.session.state !== 'cancelled';
  }
}

export default voiceOrderingService;
